import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { MessageBar, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import {
    ArrowDown20Filled,
    ArrowUp20Filled,
    Scales20Filled
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import * as ComponentFunctions from './CashFlow.DebtIndicators.Functions';

import './CashFlow.DebtIndicators.Styles.css';
import { DebtSummary } from '../../../models/Entities/CashFlow/Debt/DebtSummary';

type CardKind = 'pay' | 'collect';

const formatCurrency = (amount: number, currencyCode: string | null = 'ARS'): string => {
    const code = currencyCode ?? 'ARS';
    try {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: code,
            maximumFractionDigits: 2
        }).format(amount || 0);
    } catch {
        return `${code} ${(amount || 0).toFixed(2)}`;
    }
};

const sumCount = (rows: DebtSummary[]): number =>
    rows.reduce((acc, r) => acc + (r.pendingCount || 0), 0);

const sortRows = (rows: DebtSummary[]): DebtSummary[] =>
    [...rows].sort((a, b) =>
        (a.currencyCode || 'ARS').localeCompare(b.currencyCode || 'ARS')
    );

const CashFlowDebtIndicatorsComponent = (props: ComponentFunctions.Props) => {
    const [t] = useTranslation();

    useEffect(() => {
        if (props.UserId && !props.useLocalIndicators) {
            props.GetDebtIndicatorsByUserId(props.UserId);
        }
    }, [props.UserId, props.useLocalIndicators]);

    const payRows = useMemo(
        () => sortRows(props.indicators?.pendingToPay || []),
        [props.indicators]
    );
    const collectRows = useMemo(
        () => sortRows(props.indicators?.pendingToCollect || []),
        [props.indicators]
    );

    const payCount = sumCount(payRows);
    const collectCount = sumCount(collectRows);

    /* ── Balance per currency (collect - pay) ───────────────── */
    const balanceRows = useMemo(() => {
        const map = new Map<string, number>();
        payRows.forEach(r => {
            const code = r.currencyCode || 'ARS';
            map.set(code, (map.get(code) || 0) - (r.totalPendingAmount || 0));
        });
        collectRows.forEach(r => {
            const code = r.currencyCode || 'ARS';
            map.set(code, (map.get(code) || 0) + (r.totalPendingAmount || 0));
        });
        const codes = Array.from(map.keys()).sort((a, b) => a.localeCompare(b));
        return codes.map(code => ({ currency: code, amount: map.get(code) || 0 }));
    }, [payRows, collectRows]);

    /* ── Card total renderer ───────────────────────────────── */
    const renderTotal = (rows: DebtSummary[], variant: CardKind): React.ReactNode => {
        if (!rows || rows.length === 0) {
            return (
                <span className='cashflowDebtIndicators__total cashflowDebtIndicators__total--neutral'>
                    {t('cashflow.debtIndicators.noData', 'Sin datos')}
                </span>
            );
        }
        const totalClass =
            variant === 'pay'
                ? 'cashflowDebtIndicators__total cashflowDebtIndicators__total--pay'
                : 'cashflowDebtIndicators__total cashflowDebtIndicators__total--collect';
        if (rows.length === 1) {
            return (
                <span className={totalClass}>
                    {formatCurrency(rows[0].totalPendingAmount, rows[0].currencyCode)}
                </span>
            );
        }
        return (
            <span className='cashflowDebtIndicators__total cashflowDebtIndicators__total--neutral'>
                {t('cashflow.debtIndicators.multipleCurrencies', 'Múltiples monedas')}
            </span>
        );
    };

    const renderBreakdown = (rows: DebtSummary[], variant: CardKind) => {
        if (!rows || rows.length === 0) {
            return (
                <div className='cashflowDebtIndicators__empty'>
                    {t('cashflow.debtIndicators.noBreakdown', 'Sin movimientos pendientes')}
                </div>
            );
        }
        const badgeClass =
            variant === 'pay'
                ? 'cashflowDebtIndicators__rowBadge cashflowDebtIndicators__rowBadge--pay'
                : 'cashflowDebtIndicators__rowBadge cashflowDebtIndicators__rowBadge--collect';
        return rows.map(item => {
            const code = item.currencyCode || 'ARS';
            return (
                <div
                    className='cashflowDebtIndicators__row'
                    key={`${variant}-${code}-${item.pendingCount}`}
                >
                    <span className='cashflowDebtIndicators__rowLeft'>
                        <span className={badgeClass}>{code}</span>
                        <span className='cashflowDebtIndicators__rowCount'>
                            {item.pendingCount} {item.pendingCount === 1
                                ? t('cashflow.debtIndicators.item', 'ítem')
                                : t('cashflow.debtIndicators.items', 'ítems')}
                        </span>
                    </span>
                    <span className='cashflowDebtIndicators__rowAmount'>
                        {formatCurrency(item.totalPendingAmount, code)}
                    </span>
                </div>
            );
        });
    };

    const renderBalance = () => {
        if (balanceRows.length === 0) {
            return (
                <div className='cashflowDebtIndicators__empty'>
                    {t('cashflow.debtIndicators.noBreakdown', 'Sin movimientos pendientes')}
                </div>
            );
        }
        return balanceRows.map(({ currency, amount }) => {
            const isPositive = amount >= 0;
            const amountClass = isPositive
                ? 'cashflowDebtIndicators__rowAmount cashflowDebtIndicators__total--balancePos'
                : 'cashflowDebtIndicators__rowAmount cashflowDebtIndicators__total--balanceNeg';
            return (
                <div className='cashflowDebtIndicators__row' key={`bal-${currency}`}>
                    <span className='cashflowDebtIndicators__rowLeft'>
                        <span className='cashflowDebtIndicators__rowBadge'>{currency}</span>
                    </span>
                    <span className={amountClass}>
                        {formatCurrency(amount, currency)}
                    </span>
                </div>
            );
        });
    };

    return (
        <div className='cashflowDebtIndicators'>
            <div className='cashflowDebtIndicators__header'>
                <div className='cashflowDebtIndicators__headerLeft'>
                    <span className='cashflowDebtIndicators__title'>
                        {t('cashflow.debtIndicators.title', 'Indicadores de deuda')}
                    </span>
                    <span className='cashflowDebtIndicators__subtitle'>
                        {t(
                            'cashflow.debtIndicators.subtitle',
                            'Resumen consolidado por moneda de pendientes de pago y cobro'
                        )}
                    </span>
                </div>
                {props.isLoadingIndicators && (
                    <span className='cashflowDebtIndicators__loading'>
                        <Spinner size='extra-tiny' />
                        {t('cashflow.debtIndicators.loading', 'Actualizando…')}
                    </span>
                )}
            </div>

            {props.failOnLoadingIndicators && (
                <div className='cashflowDebtIndicators__error'>
                    <MessageBar intent='error'>
                        <MessageBarBody>
                            <MessageBarTitle>
                                {t(
                                    'cashflow.debtIndicators.errorTitle',
                                    'No se pudieron cargar los indicadores'
                                )}
                            </MessageBarTitle>
                            {props.error?.ErrorMessage}
                        </MessageBarBody>
                    </MessageBar>
                </div>
            )}

            <div className='cashflowDebtIndicators__grid'>
                {/* Pending to pay */}
                <div className='cashflowDebtIndicators__card cashflowDebtIndicators__card--pay'>
                    <div className='cashflowDebtIndicators__cardHeader'>
                        <span className='cashflowDebtIndicators__cardIcon cashflowDebtIndicators__cardIcon--pay'>
                            <ArrowUp20Filled />
                        </span>
                        <span className='cashflowDebtIndicators__cardLabel'>
                            <span className='cashflowDebtIndicators__cardLabelMain'>
                                {t('cashflow.debtIndicators.pendingToPay', 'Pendiente de pago')}
                            </span>
                            <span className='cashflowDebtIndicators__cardLabelSub'>
                                {t('cashflow.debtIndicators.pendingToPaySub', 'Egresos comprometidos')}
                            </span>
                        </span>
                    </div>
                    {renderTotal(payRows, 'pay')}
                    {payCount > 0 && (
                        <span className='cashflowDebtIndicators__countChip'>
                            {payCount} {payCount === 1
                                ? t('cashflow.debtIndicators.pending', 'pendiente')
                                : t('cashflow.debtIndicators.pendings', 'pendientes')}
                        </span>
                    )}
                    <div className='cashflowDebtIndicators__breakdown'>
                        {renderBreakdown(payRows, 'pay')}
                    </div>
                </div>

                {/* Pending to collect */}
                <div className='cashflowDebtIndicators__card cashflowDebtIndicators__card--collect'>
                    <div className='cashflowDebtIndicators__cardHeader'>
                        <span className='cashflowDebtIndicators__cardIcon cashflowDebtIndicators__cardIcon--collect'>
                            <ArrowDown20Filled />
                        </span>
                        <span className='cashflowDebtIndicators__cardLabel'>
                            <span className='cashflowDebtIndicators__cardLabelMain'>
                                {t('cashflow.debtIndicators.pendingToCollect', 'Pendiente de cobro')}
                            </span>
                            <span className='cashflowDebtIndicators__cardLabelSub'>
                                {t('cashflow.debtIndicators.pendingToCollectSub', 'Ingresos por percibir')}
                            </span>
                        </span>
                    </div>
                    {renderTotal(collectRows, 'collect')}
                    {collectCount > 0 && (
                        <span className='cashflowDebtIndicators__countChip'>
                            {collectCount} {collectCount === 1
                                ? t('cashflow.debtIndicators.pending', 'pendiente')
                                : t('cashflow.debtIndicators.pendings', 'pendientes')}
                        </span>
                    )}
                    <div className='cashflowDebtIndicators__breakdown'>
                        {renderBreakdown(collectRows, 'collect')}
                    </div>
                </div>

                {/* Net balance */}
                <div className='cashflowDebtIndicators__card cashflowDebtIndicators__card--balance'>
                    <div className='cashflowDebtIndicators__cardHeader'>
                        <span className='cashflowDebtIndicators__cardIcon cashflowDebtIndicators__cardIcon--balance'>
                            <Scales20Filled />
                        </span>
                        <span className='cashflowDebtIndicators__cardLabel'>
                            <span className='cashflowDebtIndicators__cardLabelMain'>
                                {t('cashflow.debtIndicators.netBalance', 'Saldo neto')}
                            </span>
                            <span className='cashflowDebtIndicators__cardLabelSub'>
                                {t('cashflow.debtIndicators.netBalanceSub', 'Cobros menos pagos por moneda')}
                            </span>
                        </span>
                    </div>
                    <div className='cashflowDebtIndicators__breakdown' style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
                        {renderBalance()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(CashFlowDebtIndicatorsComponent as any);
